/**
 * Ratings View — Credit and investment ratings from Nigerian and international rating agencies.
 *
 * Comprehensive coverage of all NGX-listed stocks, bonds, and ETFs with ratings from:
 *   - Agusto & Co.: Nigeria's first SEC-registered credit rating agency
 *   - GCR Ratings: Major African rating agency (acquired by Moody's)
 *   - DataPro Limited: SEC-licensed credit rating agency
 *   - Fitch Ratings: Major international rating agency
 */
import { useState, useMemo } from 'react';
import { getAllNGXRatings } from '../lib/ngx-ratings';

interface Rating {
  agency: string;
  symbol: string;
  company?: string;
  assetType: 'Stock' | 'Bond' | 'ETF';
  rating: string;
  outlook: 'Stable' | 'Positive' | 'Negative' | 'Watch';
  ratingDate: string;
  nextReview: string;
  rationale: string;
}

export function RatingsView() {
  const [selectedAsset, setSelectedAsset] = useState<'Stock' | 'Bond' | 'ETF'>('Stock');
  const [searchSymbol, setSearchSymbol] = useState('');

  const allRatings = useMemo(() => getAllNGXRatings(), []);

  const filteredRatings = useMemo(() => {
    return allRatings.filter((r) => {
      const matchesAsset = r.assetType === selectedAsset;
      const matchesSymbol = r.symbol.toLowerCase().includes(searchSymbol.toLowerCase());
      return matchesAsset && matchesSymbol;
    });
  }, [selectedAsset, searchSymbol, allRatings]);

  const uniqueSymbols = useMemo(() => {
    return Array.from(
      new Set(
        allRatings.filter((r) => r.assetType === selectedAsset).map((r) => r.symbol)
      )
    ).sort();
  }, [selectedAsset, allRatings]);

  const getRatingColor = (rating: string): string => {
    const ratingChar = rating.charAt(0);
    if (ratingChar === 'A') return 'var(--green)';
    if (ratingChar === 'B') return 'var(--warn)';
    if (ratingChar === 'C') return 'var(--red)';
    return 'var(--muted)';
  };

  const getOutlookColor = (outlook: string): string => {
    if (outlook === 'Positive') return 'var(--green)';
    if (outlook === 'Negative') return 'var(--red)';
    if (outlook === 'Watch') return 'var(--warn)';
    return 'var(--muted)';
  };

  return (
    <div className="ratings-view">
      <div className="ratings-head">
        <div>
          <h2>Credit & Investment Ratings</h2>
          <div className="ratings-sub">
            Compare ratings from Agusto & Co., GCR Ratings, DataPro Limited, and Fitch Ratings for Nigerian stocks, bonds, and ETFs.
          </div>
        </div>
      </div>

      <div className="ratings-controls">
        <div className="ratings-asset-filter">
          <span className="ratings-label">Asset Type:</span>
          {(['Stock', 'Bond', 'ETF'] as const).map((type) => (
            <button
              key={type}
              className={`ratings-filter-btn ${selectedAsset === type ? 'active' : ''}`}
              onClick={() => {
                setSelectedAsset(type);
                setSearchSymbol('');
              }}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="ratings-search">
          <input
            type="text"
            placeholder={`Search ${selectedAsset.toLowerCase()}...`}
            value={searchSymbol}
            onChange={(e) => setSearchSymbol(e.target.value)}
            className="ratings-search-input"
          />
        </div>
      </div>

      {uniqueSymbols.length > 0 && (
        <div className="ratings-quicklinks">
          <span className="ratings-label">Quick access:</span>
          <div className="ratings-quicklink-list">
            {uniqueSymbols.slice(0, 8).map((sym) => (
              <button
                key={sym}
                className={`ratings-quicklink ${searchSymbol === sym ? 'active' : ''}`}
                onClick={() => setSearchSymbol(sym)}
              >
                {sym}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ratings-table-wrapper">
        <div className="ratings-table">
          <div className="ratings-row ratings-header">
            <div className="rc-symbol">Symbol</div>
            <div className="rc-company">Company</div>
            <div className="rc-agency">Agency</div>
            <div className="rc-rating">Rating</div>
            <div className="rc-outlook">Outlook</div>
            <div className="rc-date">Date</div>
            <div className="rc-rationale">Rationale</div>
          </div>

          {filteredRatings.length > 0 ? (
            filteredRatings.map((r, idx) => (
              <div key={idx} className="ratings-row">
                <div className="rc-symbol">
                  <strong>{r.symbol}</strong>
                </div>
                <div className="rc-company">
                  <span className="rc-company-name">{r.company || 'N/A'}</span>
                </div>
                <div className="rc-agency">
                  <span className="rc-agency-name">{r.agency}</span>
                </div>
                <div className="rc-rating">
                  <span
                    className="rc-rating-badge"
                    style={{ color: getRatingColor(r.rating) }}
                  >
                    {r.rating}
                  </span>
                </div>
                <div className="rc-outlook">
                  <span
                    className="rc-outlook-badge"
                    style={{ color: getOutlookColor(r.outlook) }}
                  >
                    {r.outlook}
                  </span>
                </div>
                <div className="rc-date mono">{r.ratingDate}</div>
                <div className="rc-rationale">{r.rationale}</div>
              </div>
            ))
          ) : (
            <div className="ratings-empty">No ratings found for {searchSymbol || 'this'} {selectedAsset.toLowerCase()}.</div>
          )}
        </div>
      </div>

      <div className="ratings-footer">
        <div className="ratings-note">
          <strong>Rating Scale:</strong> AAA (Highest) → AA → A → BBB → BB → B → CCC → CC → C (Lowest)
        </div>
        <div className="ratings-agencies">
          <strong>Agencies:</strong>
          <ul style={{ margin: '4px 0 0 16px', fontSize: '12px' }}>
            <li><strong>Agusto & Co.</strong> — Nigeria's first SEC-registered credit rating agency</li>
            <li><strong>GCR Ratings</strong> — Major African rating agency (acquired by Moody's)</li>
            <li><strong>DataPro Limited</strong> — SEC-licensed credit rating agency</li>
            <li><strong>Fitch Ratings</strong> — Major international rating agency</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
